import {
  CalendarFilled,
  DeleteFilled,
  DollarCircleOutlined,
  HeatMapOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Empty, Input } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useCreateTrip } from "../../api/api";
import { useDeleteTrip } from "../../api/api.delete";
import { useGetAllTrips, useGetSubscriptionLimits } from "../../api/api.get";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { capitalize } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";

export default function Trips() {
  const navigate = useNavigate();
  const { defaultGroupId } = useGroupContext();

  const { mutateAsync } = useCreateTrip(defaultGroupId);
  const { currency } = useCurrencyContext();
  const { data: subscriptionLimitData } = useGetSubscriptionLimits();

  const { data: trips, refetch: refetchTrips } = useGetAllTrips(defaultGroupId);

  const { notification, notifyError, notifySuccess } = useNotifications();
  const { mutateAsync: deleteAsync } = useDeleteTrip(defaultGroupId);

  const handleDeleteTrip = (id: any, e: any) => {
    e.preventDefault();
    deleteAsync({ tripId: id })
      .then(() => refetchTrips())
      .catch(() => notifyError("Please try again"));
  };

  const formik = useFormik({
    initialValues: {
      destination: "",
      fromDate: "",
      name: "",
      toDate: "",
    },
    validationSchema: Yup.object({
      destination: Yup.string().required("Please enter destination"),
      fromDate: Yup.string().required("Please select From date"),
      toDate: Yup.string().required("Please select To date"),
      name: Yup.string().required("Please enter name"),
    }),
    onSubmit: async (values) => {
      const days = dayjs(values.toDate).diff(dayjs(values.fromDate), "d");

      if (days < 0) {
        notifyError("End date is later than start date.");
        return;
      }
      mutateAsync({
        destination: values.destination,
        fromDate: dayjs(values.fromDate).format("YYYY-MM-DD"),
        name: values.name,
        toDate: dayjs(values.toDate).format("YYYY-MM-DD"),
      })
        .then(async () => {
          await refetchTrips();
          notifySuccess({ description: "Trip successfully created" });
          formik.resetForm();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Trips</h1>
            <p className="text-muted-foreground">
              Manage your trips and track expenses for each journey
            </p>
          </div>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Trips List */}

            <div className="lg:col-span-2">
              {!trips ||
                (trips.length === 0 && (
                  <Empty description="No Trips Found" className="mt-10" />
                ))}

              {trips && trips.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Your Trips
                    </CardTitle>
                    <CardDescription>
                      {trips?.length} trip{trips?.length !== 1 ? "s" : ""}{" "}
                      recorded
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {trips.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground">
                            No trips yet. Create your first trip!
                          </p>
                        </div>
                      ) : (
                        trips.map((trip) => {
                          const days = dayjs(trip.toDate).diff(
                            trip.fromDate,
                            "d",
                          );
                          return (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/trips/${trip.id}`);
                              }}
                              key={trip.id}
                              className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition cursor-pointer bg-slate-50 hover:bg-slate-100"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-foreground text-lg mb-1">
                                    {capitalize(trip.name)}
                                  </h3>
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                                    <HeatMapOutlined className="w-4 h-4" />
                                    {trip.destination}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    icon={<DeleteFilled />}
                                    type="text"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTrip(trip.id, e);
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Trip Details */}
                              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200">
                                <div className="flex items-center gap-2">
                                  <CalendarFilled className="w-4 h-4 text-primary" />
                                  <div className="flex flex-row items-center gap-2">
                                    <p className="text-xs text-muted-foreground">
                                      Duration
                                    </p>
                                    <p className="text-sm font-semibold text-foreground">
                                      {days} days
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <DollarCircleOutlined className="w-4 h-4 text-primary" />
                                  <div className="flex flex-row gap-2 items-center">
                                    <p className="text-xs text-muted-foreground">
                                      Total Spent
                                    </p>
                                    <p className="text-sm font-semibold text-foreground">
                                      {currency}
                                      {toFixedDisplay(trip.total)}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Date Range */}
                              <div className="text-xs text-muted-foreground mt-3">
                                {dayjs(trip.fromDate).format("DD MMM YYYY")} -{" "}
                                {dayjs(trip.toDate).format("DD MMM YYYY")}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel: Add Trip Form */}
            <div>
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Add New Trip</CardTitle>
                  <CardDescription>Plan your next adventure</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    {/* Trip Name */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Trip Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Summer Vacation"
                        {...formik.getFieldProps("name")}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Give your trip a memorable name
                      </p>
                    </div>

                    {/* Destination */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Destination *
                      </label>
                      <Input
                        placeholder="e.g., Bali, Indonesia"
                        {...formik.getFieldProps("destination")}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Where are you traveling to?
                      </p>
                    </div>

                    {/* From Date */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        From Date *
                      </label>
                      <DatePicker
                        onChange={(date) =>
                          formik.setFieldValue("fromDate", date)
                        }
                        value={formik.values.fromDate}
                        status={!!formik.errors.fromDate ? "error" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Start date of your trip
                      </p>
                    </div>

                    {/* To Date */}
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        To Date *
                      </label>
                      <DatePicker
                        onChange={(date) =>
                          formik.setFieldValue("toDate", date)
                        }
                        value={formik.values.toDate}
                        status={!!formik.errors.toDate ? "error" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        End date of your trip
                      </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between gap-2 pt-4">
                      <Button
                        className="flex-1"
                        onClick={formik.submitForm}
                        type="primary"
                        size="large"
                        disabled={Object.keys(formik.errors).length > 0}
                      >
                        Add Trip
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={() => formik.resetForm()}
                        type="text"
                        size="large"
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              <p className="gap-2 mt-6 px-6 py-4 border-amber-300 border bg-orange-100 rounded-xl text-xs">
                Your subscription allows you to add upto{" "}
                <strong>
                  {subscriptionLimitData?.tripsPerMonth ?? 1} trip(s)
                </strong>{" "}
                per month. To add more, please{" "}
                <Link to={"/subscriptions"} className="underline text-blue-700">
                  upgrade
                </Link>{" "}
                your subscription or wait until the 1st of next month.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
